/**
 * ATT (App Tracking Transparency) bridge.
 *
 * On native iOS (Capacitor), request ATT permission at launch — required by
 * Apple guideline 5.1.2(i) whenever the App Store Connect privacy manifest
 * declares that any user data (name, phone, email) is collected for tracking.
 *
 * REQUIRED iOS PROJECT CONFIG (must be present in Info.plist):
 *   <key>NSUserTrackingUsageDescription</key>
 *   <string>We use this to personalize your wealth strategy recommendations
 *   and improve the accuracy of your calculator projections.</string>
 *
 * If NSUserTrackingUsageDescription is missing, iOS silently refuses to show
 * the dialog and this call resolves to 'notDetermined' — which is exactly
 * what Apple reviewers flag under 5.1.2(i).
 *
 * On web / non-Capacitor runtimes this is a no-op.
 */

function isNativeIOS() {
  if (typeof window === "undefined") return false;
  const cap = window.Capacitor;
  if (!cap || typeof cap.getPlatform !== "function") return false;
  return cap.getPlatform() === "ios";
}

/**
 * Ask ATT permission every launch until the OS has a resolved answer.
 * Once the user responds (authorized/denied/restricted) the OS caches the
 * answer and subsequent calls return immediately without showing the dialog.
 *
 * Returns: 'authorized' | 'denied' | 'restricted' | 'notDetermined' |
 *          'unavailable' | 'error'
 *
 * Safe to call from anywhere; always resolves, never throws.
 */
export async function requestTrackingPermissionOnce() {
  if (typeof window === "undefined") return "unavailable";
  if (!isNativeIOS()) return "unavailable";

  try {
    const mod = await import(
      /* webpackIgnore: true */ "@capgo/capacitor-app-tracking-transparency"
    ).catch(() => null);
    if (!mod || !mod.AppTrackingTransparency) {
      // eslint-disable-next-line no-console
      console.warn("[ATT] plugin not linked — check `npx cap sync ios`");
      return "unavailable";
    }

    // Read current status. iOS returns 'notDetermined' until the user has
    // seen and dismissed the ATT prompt at least once.
    let status = "notDetermined";
    try {
      const s = await mod.AppTrackingTransparency.getStatus();
      status = s?.status || status;
    } catch { /* getStatus may not exist on older plugin versions */ }

    // If undetermined, present the OS dialog. Idempotent when already set.
    if (status === "notDetermined" || !status) {
      const r = await mod.AppTrackingTransparency.requestPermission();
      status = r?.status || status;
    }
    // eslint-disable-next-line no-console
    console.log("[ATT] status =", status);
    return status;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[ATT] request failed", err);
    return "error";
  }
}
